import {
  Location as AngularLocation,
  NgIf,
  NgFor,
  NgClass,
  AsyncPipe,
  LowerCasePipe,
} from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import {
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDatepicker,
} from '@angular/material/datepicker';
import { MatFormField, MatLabel, MatSuffix, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { ActivatedRoute, Router } from '@angular/router';
import { RootState } from '@next/core-lib/app';
import { PageDetailComponent } from '@next/core-lib/components';
import { DIALOG_RETURN_TYPES, DialogService } from '@next/core-lib/dialog';
import { AgrConnectFormDirective, AgrNumberDirective } from '@next/core-lib/form';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { RouterSelectors } from '@next/core-lib/router-store';
import { Store, select } from '@ngrx/store';
import { Observable, Subject, combineLatest } from 'rxjs';
import { map, shareReplay, skipWhile, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import { VatPercentages } from 'src/app/shared/constants';
import Utils from 'src/app/shared/utils';

import { CostItemComponent } from './cost-item/cost-item.component';
import { CostItemForm } from './cost-item/cost-item.model';
import { CostTotalComponent } from './cost-total/cost-total.component';
import { CostTotalForm } from './cost-total/cost-total.model';
import { getBarns } from '../../../settings/store/selectors/setting.selector';
import { Barn, Breed, Cycle, DayOldChick, Hatchery } from '../../models/cycle.model';
import * as cycleActions from '../../store/actions/cycle.actions';
import {
  getConcepts,
  getCycles,
  getHatcheries,
  getSelectedCycle,
} from '../../store/selectors/cycle.selectors';
import {
  getBarnsInUse,
  getNextDayOldChickId,
  getPreviousDayOldChick,
  getSelectedDayOldChick,
} from '../../store/selectors/day-old-chick.selectors';
import { dayOldChickAmountValidator } from '../../validators/day-old-chick-amount-validator';

@Component({
  selector: 'app-day-old-chick-item',
  templateUrl: 'day-old-chick-item.component.html',
  styles: [
    `
      .form-field-prefix {
        padding-right: 10px;
      }

      .arrival-notification {
        height: 40px;
        font-style: italic;
      }
    `,
  ],
  imports: [
    PageDetailComponent,
    NgIf,
    ReactiveFormsModule,
    AgrConnectFormDirective,
    MatFormField,
    MatLabel,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatError,
    MatSelect,
    NgFor,
    MatOption,
    AgrNumberDirective,
    MatSlideToggle,
    CostItemComponent,
    CostTotalComponent,
    NgClass,
    MatButton,
    AsyncPipe,
    LowerCasePipe,
    TranslatePipe,
  ],
})
export class DayOldChickItemComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<RootState>>(Store);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly locale = inject(LocaleService);
  private readonly dialogService = inject(DialogService);
  private readonly location = inject(AngularLocation);

  routeUrl$: Observable<string> = this.store.pipe(select(RouterSelectors.getPath));

  pageTitle$: Observable<string> = this.store.pipe(
    withLatestFrom(this.routeUrl$),
    map(([_, url]) =>
      url.endsWith('/day-old-chick/add')
        ? this.locale.translate('add') +
          ' ' +
          this.locale.translate('cycle-navigation.day-old-chick')
        : this.locale.translate('edit-day-old-chick'),
    ),
  );

  cycle$: Observable<Cycle> = this.store.select(getSelectedCycle);
  hatcheries$: Observable<Hatchery[]> = this.store.select(getHatcheries);
  dayOldChick$: Observable<DayOldChick> = this.store.select(getSelectedDayOldChick);
  nextDayOldChickId$: Observable<number> = this.store.select(getNextDayOldChickId);

  barns$: Observable<Barn[]> = this.store.select(getBarnsInUse).pipe(
    withLatestFrom(this.store.select(getBarns)),
    skipWhile(([barnsInUse, barns]) => barnsInUse === undefined || barns === undefined),
    map(([barnsInUse, barns]) => {
      const differentBarns = barnsInUse ? barnsInUse.filter((b) => barns.includes(b)) : [];
      if (differentBarns.length > 0) {
        return [...barns, ...differentBarns]
          .filter((b) => b.isActive)
          .sort((a, b) => (a.name > b.name ? 1 : -1));
      }
      return barns.filter((barn) => barn.isActive);
    }),
  );

  breeds$: Observable<Breed[]> = combineLatest([
    this.store.select(getSelectedCycle),
    this.store.select(getConcepts),
  ]).pipe(
    map(([cycle, concepts]) => {
      if (cycle && concepts) {
        const concept = concepts.find((c) => c.id === cycle.concept?.id);
        if (concept) {
          return concept.breeds;
        }
        return [];
      }
    }),
  );

  dayOldChickNotNull$: Observable<DayOldChick> = this.dayOldChick$.pipe(
    skipWhile((chick) => !chick),
    shareReplay(1),
    take(1),
    map((o) => ({
      ...o,
      chicksPrice: o.chicksPrice != null ? o.chicksPrice : null,
      amounts: o.chicksPrice != null,
    })),
  );
  barn = new FormControl(null, [Validators.required]);

  form = new FormGroup({
    id: new FormControl(null, []),
    arrivalDate: new FormControl(null, [Validators.required]),
    breed: new FormControl<Breed>(null, [Validators.required]),
    parentStockIdentifier: new FormControl(null, []),
    hatchery: new FormControl<Hatchery>(null, [Validators.required]),
    chickBirthDate: new FormControl(null, [Validators.required]),
    barn: this.barn,
    amountOfChicks: new FormControl(
      null,
      [Validators.required, Validators.max(2147483647)],
      dayOldChickAmountValidator(this.store, this.barn),
    ),
    amounts: new FormControl<boolean>(true, [Validators.required]),
    price: new FormGroup<CostItemForm>({
      chickCost: new FormControl<number>(null, []),
      chicksTotalCost: new FormControl<number>(null, []),
      chicksTotalCostIncludingVat: new FormControl<number>(null, []),
    }),

    vaccination: new FormGroup<CostItemForm>({
      chickCost: new FormControl<number>(null, []),
      chicksTotalCost: new FormControl<number>(null, []),
      chicksTotalCostIncludingVat: new FormControl<number>(null, []),
    }),

    discount: new FormGroup<CostItemForm>({
      chickCost: new FormControl<number>(null, []),
      chicksTotalCost: new FormControl<number>(null, []),
      chicksTotalCostIncludingVat: new FormControl<number>(null, []),
    }),

    diverse: new FormGroup<CostItemForm>({
      chickCost: new FormControl<number>(null, []),
      chicksTotalCost: new FormControl<number>(null, []),
      chicksTotalCostIncludingVat: new FormControl<number>(null, []),
    }),

    calculatedAmounts: new FormGroup<CostTotalForm>({
      calculatedAmounts: new FormControl<number>(null, []),
      calculatedAmountsIncludingVat: new FormControl<number>(null, []),
    }),

    chicksWeightInGrams: new FormControl(null, []),
    chicksParentsAgeInWeeks: new FormControl(null, []),
    invoiceDate: new FormControl<Date>(null, []),
    invoiceNumber: new FormControl<string>(null, [Validators.maxLength(50)]),
    vatPercentage: new FormControl<number>(null, []),
  });
  unsubscribeSignal: Subject<void> = new Subject<void>();
  vatPercentages = VatPercentages;

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}

  ngOnInit() {
    this.form.controls.arrivalDate.valueChanges
      .pipe(takeUntil(this.unsubscribeSignal.asObservable()))
      .subscribe((date: Date) => {
        if (!this.form.controls.chickBirthDate.value && date) {
          this.form.controls.chickBirthDate.patchValue(date);
        }

        if (date) {
          this.form.controls.invoiceDate.patchValue(date);
        }
      });

    this.form.controls.amounts.valueChanges
      .pipe(takeUntil(this.unsubscribeSignal.asObservable()))
      .subscribe((amounts: boolean) => {
        if (!amounts) {
          this.turnOffAmountsToggle();
        } else {
          this.turnOnAmountsToggle();
        }
      });

    this.dayOldChick$
      .pipe(
        withLatestFrom(this.store.select(getPreviousDayOldChick), this.barns$, this.routeUrl$),
        skipWhile(
          ([_dayOldChick, _previousDayOldChick, barns, _url]: [
            DayOldChick,
            DayOldChick,
            Barn[],
            string,
          ]) => barns === undefined,
        ),
        take(1),
      )
      .subscribe(
        ([dayOldChick, previousDayOldChick, barns, url]: [
          DayOldChick,
          DayOldChick,
          Barn[],
          string,
        ]) => {
          if (dayOldChick) {
            this.form.controls.price.controls.chickCost.patchValue(dayOldChick.chicksPrice);
            this.form.controls.vaccination.controls.chickCost.patchValue(
              dayOldChick.vaccinationPricePerChick,
            );
            this.form.controls.discount.controls.chickCost.patchValue(
              dayOldChick.discountPricePerChick,
            );
            this.form.controls.diverse.controls.chickCost.patchValue(
              dayOldChick.diversePricePerChick,
            );
          }

          if (url.endsWith('/day-old-chick/add')) {
            if (barns.length > 0) {
              this.form.controls.barn.patchValue(barns[0]);
            }
            if (previousDayOldChick) {
              if (previousDayOldChick.hatchery) {
                this.form.controls.hatchery.patchValue(previousDayOldChick.hatchery);
              }
              if (previousDayOldChick.breed) {
                this.form.controls.breed.patchValue(previousDayOldChick.breed);
              }
            }
          }
        },
      );

    this.store
      .select(getCycles)
      .pipe(withLatestFrom(this.cycle$), take(1))
      .subscribe(([allCycles, currentCycle]) => {
        const lastCyclesWithDayOldChicks = allCycles
          .map((cycle) => {
            if (
              !!cycle.dayOldChicks &&
              cycle.dayOldChicks.some(
                (dayOldChick) => dayOldChick.hatchery !== null && dayOldChick.breed !== null,
              )
            ) {
              return cycle;
            }
          })
          .slice()
          .sort((a, b) => (a.cycleDate < b.cycleDate ? 1 : -1));

        const lastCycleWithDayOldChicks =
          lastCyclesWithDayOldChicks.length > 0 ? lastCyclesWithDayOldChicks[0] : undefined;

        if (lastCycleWithDayOldChicks) {
          this.form.controls.hatchery.patchValue(
            lastCycleWithDayOldChicks.dayOldChicks[0].hatchery,
          );

          const lastUsedBreed = lastCycleWithDayOldChicks.dayOldChicks[0].breed;
          const initialBreed =
            currentCycle.concept?.breeds.find(
              (b) => lastUsedBreed && b.id == lastUsedBreed.id && b.name == lastUsedBreed.name,
            ) ?? undefined;
          this.form.controls.breed.patchValue(initialBreed);
        }
      });
  }

  ngOnDestroy() {
    this.unsubscribeSignal.next();
    this.unsubscribeSignal.unsubscribe();
  }

  back() {
    if (this.router.navigated) {
      this.location.back();
    } else {
      this.router.navigate(['../'], { relativeTo: this.activatedRoute });
    }
  }

  changeArrivalDate() {
    const date = this.form.controls.arrivalDate.value;
    this.form.controls.chickBirthDate.patchValue(date);
    this.form.controls.invoiceDate.patchValue(date);
  }

  compareBarns = (a1: Barn, a2: Barn) => a1 && a2 && a1.id === a2.id;

  compareBreeds = (b1: Breed, b2: Breed) => b1 && b2 && b1.id === b2.id;

  compareHatcheries = (h1: Hatchery, h2: Hatchery) => h1 && h2 && h1.id === h2.id;

  save() {
    if (this.form.valid) {
      this.cycle$
        .pipe(withLatestFrom(this.dayOldChick$, this.nextDayOldChickId$), take(1))
        .subscribe(([cycle, dayOldChick, nextId]) => {
          const formValue = {
            ...this.form.value,
            amounts: this.form.value.amounts,
            barn: this.form.value.barn,
            breed: this.form.value.breed,
            hatchery: this.form.value.hatchery,
            arrivalDate: Utils.parseDateTimeFromFormValue(this.form.controls.arrivalDate.value),
            chickBirthDate: Utils.parseDateTimeFromFormValue(
              this.form.controls.chickBirthDate.value,
            ),
            amountOfChicks: parseInt(<string>this.form.controls.amountOfChicks.value, 10),
            chicksPrice: this.getFormValueIfAmountsEnabled(
              this.form.controls.price.controls.chickCost,
            ),
            vaccinationPricePerChick: this.getFormValueIfAmountsEnabled(
              this.form.controls.vaccination.controls.chickCost,
            ),
            discountPricePerChick: this.getFormValueIfAmountsEnabled(
              this.form.controls.discount.controls.chickCost,
            ),
            diversePricePerChick: this.getFormValueIfAmountsEnabled(
              this.form.controls.diverse.controls.chickCost,
            ),
            invoiceDate: Utils.parseDateTimeFromFormValue(this.form.controls.invoiceDate.value),
            invoiceNumber: this.form.controls.invoiceNumber.value,
            vatPercentage: this.form.controls.vatPercentage.value,
          };

          if (dayOldChick) {
            // check if the barn is changed and the old barn was used in the balance
            let isUsedInBalance = false;
            if (dayOldChick.barn.id !== this.form.controls.barn.value.id) {
              isUsedInBalance =
                cycle.balancesPerDeliveryLine.findIndex((line) =>
                  line.barnBalances.find(
                    (barnbalance) => barnbalance.barnId === dayOldChick.barn.id,
                  ),
                ) !== -1;
            }
            if (isUsedInBalance) {
              this.dialogService
                .confirmWarning({
                  text: this.locale.translate('edit-day-old-chick_barn-used-in-balance'),
                })
                .subscribe((response) => {
                  if (response.status === DIALOG_RETURN_TYPES.confirm) {
                    this.store.dispatch(
                      cycleActions.editDayOldChick({
                        cycleId: cycle.id,
                        dayOldChick: { ...formValue, id: dayOldChick.id },
                      }),
                    );
                  }
                });
            } else {
              this.store.dispatch(
                cycleActions.editDayOldChick({
                  cycleId: cycle.id,
                  dayOldChick: { ...formValue, id: dayOldChick.id },
                }),
              );
            }
          } else {
            this.store.dispatch(
              cycleActions.addDayOldChick({
                cycleId: cycle.id,
                dayOldChick: { ...formValue, id: nextId },
              }),
            );
          }
        });
    }
  }

  getFormValueIfAmountsEnabled(formControl: FormControl<number>): number {
    return this.form.controls.amounts.value ? formControl.value : null;
  }

  turnOnAmountsToggle() {
    this.setAmountsFormValidators(this.form.controls.price);
  }

  turnOffAmountsToggle() {
    this.clearAmountsFormValidators(this.form.controls.price);
    this.clearAmountsFormValidators(this.form.controls.vaccination);
    this.clearAmountsFormValidators(this.form.controls.discount);
    this.clearAmountsFormValidators(this.form.controls.diverse);
  }

  setAmountsFormValidators(formGroup: FormGroup<CostItemForm>) {
    formGroup.controls.chickCost.setValidators(Validators.required);
    formGroup.controls.chickCost.updateValueAndValidity();
    formGroup.controls.chicksTotalCost.setValidators(Validators.required);
    formGroup.controls.chicksTotalCost.updateValueAndValidity();
  }

  clearAmountsFormValidators(formGroup: FormGroup<CostItemForm>) {
    formGroup.controls.chickCost.clearValidators();
    formGroup.controls.chickCost.updateValueAndValidity();
    formGroup.controls.chicksTotalCost.clearValidators();
    formGroup.controls.chicksTotalCost.updateValueAndValidity();
  }
}
